/* eslint-disable */
import CD from 'cropduster';
import getEnv from './environment';
import { encodeURL } from './utils';
import {
  cropImageElement,
  containImageElement
} from './dom-utils/format-image';

const ENVIRONMENT = getEnv();
const ANALYTICS_BASE_URL = 'https://scratch-off.micpn.com';

export default class StudioApp {
  /**
   * Instantiate a new StudioApp. Note that if you override `constructor` in
   * a subclass, you _must_ call super() _first_ before performing any other
   * operations. It is highly recommended that you declare all of your params
   * by setting instance variables via `this.thing = this.param(...)` up front.
   */
  constructor({ container } = {}) {
    this.container = container || document;
  }

  /**
   * Wrapper around CD.param to perform validations and do some
   * type coercion.
   */
  param(key, opts = {}) {
    if (!(opts.required || opts.hasOwnProperty('defaultValue'))) {
      this.error('parameters need a default if they are not required');
    }

    let rawValue = CD.param(key);
    if (!rawValue || !rawValue.length) {
      if (opts.hasOwnProperty('defaultValue')) {
        if (typeof opts.defaultValue === 'function') {
          rawValue = opts.defaultValue();
        } else {
          rawValue = opts.defaultValue;
        }
      } else {
        this.error(`missing required query param: ${key}`);
      }
    }

    if (opts.type === 'float') {
      return parseFloat(rawValue);
    } else if (opts.type === 'integer') {
      return parseInt(rawValue, 10);
    } else {
      return rawValue;
    }
  }

  /**
   * Bail and show fallback content, alerting that something has gone wrong.
   */
  error(msg) {
    CD.throwError(msg);
    throw new Error(msg);
  }

  /**
   * Default command to perform some common actions on a Studio app. This
   * can and should be overridden by subclasses, with or without calling
   * `super()`.
   */
  render() {
    this.fillElements();
    this.waitForImageAssets();
  }

  /**
   * Find all images on the page and ensure that they get loaded before
   * render.
   */
  waitForImageAssets() {
    const imageSrcUrls = Array.from(document.querySelectorAll('img')).map(
      ({ src }) => src
    );

    const backgroundImageUrls = Array.from(
      document.querySelectorAll('[style*="background-image"]')
    ).map(({ style }) => {
      const { backgroundImage } = style;
      return backgroundImage.replace(/^url\((.*)\)$/g, '$1');
    });

    const imageUrls = imageSrcUrls
      .concat(backgroundImageUrls)
      .filter(url => url && url !== 'none');

    imageUrls.forEach(image => {
      CD.waitForAsset(image);
    });

    return imageUrls;
  }

  formatImageTags() {
    return this.allTags
      .filter(({ type }) => type === 'image')
      .forEach(({ isCropped, element }) =>
        isCropped ? cropImageElement(element) : containImageElement(element)
      );
  }

  /**
   * a flat list of all tags, associated with their DOM elements
   */
  get allTags() {
    if (this._allTags) {
      return this._allTags;
    }

    const flattenTags = (nestedTags = []) => {
      return nestedTags.reduce((flattened, tag) => {
        const { subtags } = tag;
        return [...flattened, tag, ...flattenTags(subtags)];
      }, []);
    };

    const tags = (this._allTags = flattenTags(this.tags));
    return tags;
  }

  /**
   * a list of tags with nested subtags, associated with their DOM elements
   */
  get tags() {
    if (this._tags) {
      return this._tags;
    }

    const { container } = this;
    const unassignedTags = JSON.parse(
      document.querySelector('.mi-attributes').textContent
    );

    const setElements = tags => {
      tags.forEach(tag => {
        tag.element = container.querySelector(`[mi-tag='${tag.id}']`);
        setElements(tag.subtags || []);
      });

      return tags;
    };

    const tags = (this._tags = setElements(unassignedTags));
    return tags;
  }

  /**
   * A safer way to access MI.options, via this.options.
   */
  get options() {
    if (typeof MI === 'undefined') {
      window.MI = { options: {} };
    }
    const { options } = MI;
    return options;
  }

  /**
   * A safer way to access manifest field values
   */
  get fields() {
    if (this._fields) {
      return this._fields;
    }

    const optionFields = this.options.fields || [];

    const fieldsObject = (this._fields = optionFields.reduce(
      (fields, { name, value }) => {
        fields[name] = value;
        return fields;
      },
      {}
    ));

    return fieldsObject;
  }

  setupEngagementEvents() {
    this.sendEvent('pageview');

    [10, 30, 60].forEach(t => {
      setTimeout(() => {
        this.sendEvent(`engage_${t}s`);
      }, t * 1000);
    });
  }

  sendEvent(event, params = {}) {
    if (ENVIRONMENT === 'development') {
      console.log(`Would fire ${event} event`);
      return;
    }

    try {
      const appKey = CD.params('mi_app_key');
      if (typeof appKey != 'undefined' && appKey.length) {
        console.log(`Firing ${event} event`);

        const miU = CD.params('mi_u') || '';

        const url = encodeURL(
          `${ANALYTICS_BASE_URL}/p/lp/${appKey}/t`,
          Object.assign(params, {
            event_type: event,
            mi_u: miU
          })
        );
        const image = new Image(1, 1);
        image.src = url;
      }
    } catch (e) {
      console.error(`Failed to send event ${event}`);
    }
  }
}
